import { FC, PropsWithChildren, useState, useRef, useCallback } from 'react';
import classnames from 'classnames';
import { WorkerCommentEntry } from '@scale/shared/types/chat';
import { getCommentIsOverallFeedback } from '@scale/shared/chat/response/feedback';
import { Divider, Button, Tag, Icon, TextArea } from '@scale/scaleui';
import { isNil, isFinite } from 'lodash';

const CHARACTER_LIMIT = 200;

type EditableFeedbackProps = PropsWithChildren<{
  id: string;
  workerComment: WorkerCommentEntry;
  isSelected: boolean;
  onClick: (id: string) => void;
  onDelete: (id: string) => void;
  onEdit: (id: string, text: string) => void;
}>;

// eslint-disable-next-line complexity
const EditableFeedback: FC<EditableFeedbackProps> = ({
  id,
  workerComment,
  isSelected,
  onClick,
  onDelete,
  onEdit,
}) => {
  const { comment, turn, title } = workerComment;
  const isOverallFeedback = getCommentIsOverallFeedback(workerComment);

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editedCommentText, setEditedCommentText] = useState<string>(comment);

  /*
   * The scroll to step functionality should be disabled in certain cases / when others actions are taken
   * This means we may need to stop the propagation of the click event
   */
  const isHandleClickDisabled = isOverallFeedback || isEditing;

  // Only show the first 200 characters of the comment if it exceeds the limit and add a show more button
  const [showMoreText, setShowMoreText] = useState<boolean>(false);
  const commentExceedsCharacterLimit = comment.length > CHARACTER_LIMIT;
  const commentTextToShow =
    showMoreText || !commentExceedsCharacterLimit
      ? comment
      : `${comment.slice(0, CHARACTER_LIMIT).trim()}...`;

  const handleClick = () => {
    if (isHandleClickDisabled) {
      return;
    }
    onClick(id);
  };
  const handleDelete = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    onDelete(id);
  };

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const handleTextAreaInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    e.stopPropagation();
    setEditedCommentText(e.target.value);
  };

  const onKeyDownSubmit = useCallback(
    (e: React.KeyboardEvent) => {
      e.stopPropagation();
      if (e.shiftKey && e.key === 'Enter') {
        e.preventDefault();
        if (editedCommentText.length) {
          onEdit(id, editedCommentText);
        }
        setIsEditing(false);
      }
      if (e.key === 'Escape') {
        setEditedCommentText(comment);
        setIsEditing(false);
      }
    },
    [editedCommentText, id, onEdit, comment],
  );

  return (
    <div
      className={classnames('flex flex-col bg-neutral-0 rounded-lg border', {
        'hover:cursor-pointer hover:bg-neutral-0/[.40]':
          !isOverallFeedback && !isHandleClickDisabled,
        'border-primary-300': isSelected,
        'border-neutral-300': !isSelected,
      })}
      onClick={handleClick}
    >
      <div className="flex flex-grow-0 p-3 justify-between items-center">
        <div className="flex items-center justify-start gap-2">
          {!isOverallFeedback && !isNil(turn) && turn >= 0 && (
            <Tag size="sm" variant="neutral" icon="highlighter-line">
              Turn {turn + 1}
            </Tag>
          )}
        </div>
        <div className="flex items-center justify-end">
          <div className="flex flex-row gap-1">
            <Icon
              icon={['far', 'edit']}
              onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                e.stopPropagation();
                setIsEditing(true);
              }}
              tooltip="Edit this comment"
            />
            <Icon
              icon={['far', 'trash-alt']}
              onClick={handleDelete}
              tooltip="Delete this comment"
            />
          </div>
        </div>
      </div>
      {!!title && (
        <div className="px-3 pb-3 text-neutral-700 text-sm font-semibold break-words">{title}</div>
      )}
      <div className="px-3">
        <Divider direction="horizontal" className="mb-3" />
      </div>
      <div className="px-3 pb-3 text-neutral-700 font-normal text-sm break-words">
        {!isEditing ? (
          <div className="flex flex-col gap-1 whitespace-pre-wrap">
            {commentTextToShow}
            {commentExceedsCharacterLimit && (
              <div
                className="text-primary-400 text-xs"
                onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                  e.stopPropagation();
                  setShowMoreText(!showMoreText);
                }}
              >
                {showMoreText ? 'Show less' : 'Show more'}
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="space-y-4">
              <TextArea
                className="pt-1"
                ref={textareaRef}
                id="dynamic-textarea"
                rows={1}
                onFocus={(e: React.FocusEvent<HTMLTextAreaElement>) => {
                  const textArea = e.target;
                  textArea.style.height = 'auto';
                  textArea.style.height = textArea.scrollHeight + 'px';
                  textArea.style.overflow = 'hidden';
                }}
                autoFocus
                dynamic
                resizable={false}
                onChange={handleTextAreaInputChange}
                value={editedCommentText}
                onKeyDown={onKeyDownSubmit}
                onClick={(e: React.MouseEvent<HTMLTextAreaElement>) => {
                  e.stopPropagation();
                }}
              />
              <div className="flex gap-2 justify-end">
                <Button
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    setEditedCommentText(comment);
                    setIsEditing(false);
                  }}
                  size="sm"
                  variant="neutral"
                >
                  Cancel
                </Button>
                <Button
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    if (editedCommentText.length) {
                      onEdit(id, editedCommentText);
                    }
                    setIsEditing(false);
                  }}
                  size="sm"
                  variant="primary"
                >
                  Save
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

type EditableInlineFeedbackDropdownProps = {
  workerComments: WorkerCommentEntry[];
  scrollToStep: (stepIndex: number) => void;
  setIsOpen: (isOpen: boolean) => void;
  handleSelectWorkerComment: (id: string) => void;
  selectedWorkerComment: string | null;
  onDelete: (id: string) => void;
  onEdit: (id: string, text: string) => void;
};

export const EditableInlineFeedbackDropdown: FC<EditableInlineFeedbackDropdownProps> = ({
  workerComments,
  scrollToStep,
  setIsOpen,
  handleSelectWorkerComment,
  selectedWorkerComment,
  onDelete,
  onEdit,
}: EditableInlineFeedbackDropdownProps) => {
  if (!workerComments.length) {
    return (
      <div className="border border-neutral-300 text-center text-neutral-500 bg-neutral-0 rounded-md p-2">
        You haven't left any feedback yet!
      </div>
    );
  }

  const editableFeedbackCard = workerComments.map(comment => (
    <EditableFeedback
      key={comment.workerCommentId}
      id={comment.workerCommentId}
      workerComment={comment}
      isSelected={comment.workerCommentId === selectedWorkerComment}
      onClick={(id: string) => {
        handleSelectWorkerComment(id);
        if (!isNil(comment.childId)) {
          const stepIndex = parseInt(comment.childId.split('-')[1]);
          if (isFinite(stepIndex)) {
            setIsOpen(false);
            scrollToStep(stepIndex);
          }
        }
      }}
      onDelete={onDelete}
      onEdit={onEdit}
    />
  ));
  return <>{editableFeedbackCard}</>;
};

export default EditableInlineFeedbackDropdown;
